export const articles = [
  {
    slug: 'before-you-write-an-str-ordinance',
    title: 'Before You Write an STR Ordinance',
    date: 'July 2026',
    publishedDate: '2026-07-01',
    updatedDate: '2026-08-02',
    author: 'Stacy Pobatschnig',
    authorUrl: 'https://www.linkedin.com/in/stacypobatschnig',
    summary: 'Writing a short-term rental ordinance is rarely a one-time exercise. Here\'s what communities should consider before drafting regulations.',
    content: [
      'Writing a short-term rental ordinance is rarely a one-time exercise.',
      'Many communities spend months drafting regulations, only to revisit them after discovering loopholes, unintended consequences, or requirements that are difficult to enforce.',
      'The cost of getting it wrong is not just political. It can mean more staff time, additional legal expense, repeated public hearings, and ongoing confusion for residents and property owners.',
    ],
    sections: [
      {
        heading: 'Every Community Is Different',
        paragraphs: [
          'No two short-term rental markets are identical.',
          'A mountain resort, a suburban neighborhood, and a coastal tourist town may face completely different pressures. One community may be focused on housing availability, while another is primarily concerned with noise, parking, safety, or the growth of commercial lodging in residential areas.',
          'Outside guidance can help a community choose regulations that address its actual concerns rather than borrowing a model designed for somewhere else.',
        ],
      },
      {
        heading: 'Understand the Available Regulatory Options',
        paragraphs: [
          'Short-term rental regulation is not limited to choosing between a complete ban and unrestricted operation.',
          'Communities may consider registration requirements, zoning restrictions, density limits, occupancy standards, local-contact requirements, safety rules, primary-residence requirements, or different permit categories.',
          'Each option serves a different purpose and creates different administrative demands.',
          'Someone familiar with a broad range of STR regulations can help elected officials and staff understand what each approach is designed to accomplish, where it may fall short, and how different requirements may interact.',
        ],
      },
      {
        heading: 'Consider What Can Actually Be Enforced',
        paragraphs: [
          'A regulation may sound effective during a public meeting but prove difficult to administer once it takes effect.',
          'Some rules rely on information that is easy to verify. Others require ongoing tracking, subjective judgments, or records that may not be readily available to local staff.',
          'That distinction matters.',
          'Before adopting a requirement, a community should understand what evidence will be needed, how compliance will be assessed, and whether the rule can be applied consistently. A regulation that cannot be enforced reliably may create frustration without producing the intended result.',
          'Outside guidance can help communities separate policies that are attractive in theory from those that are workable in practice.',
        ],
      },
      {
        heading: 'Avoid Unintended Consequences',
        paragraphs: [
          'Small policy choices can have large effects.',
          'Definitions, exemptions, grandfathering provisions, permit transfers, renewal standards, and appeal procedures can all change how a regulation functions.',
          'A rule intended to address one problem may unintentionally affect ordinary homeowners, create an administrative bottleneck, or open a loophole that undermines the broader policy.',
          'Considering these issues early can reduce the need for repeated amendments later.',
        ],
      },
      {
        heading: 'Think Beyond Adoption Day',
        paragraphs: [
          'Passing an ordinance is only the beginning.',
          'Staff must explain the rules, process applications, handle renewals, answer questions, respond to complaints, and apply the requirements consistently.',
          'A successful regulatory framework must reflect the community\'s goals while also being practical for staff and understandable to the people expected to follow it.',
          'Thinking through implementation before adoption can save years of rework.',
        ],
      },
      {
        heading: 'Build Public Confidence',
        paragraphs: [
          'Short-term rental debates often involve strong and competing opinions.',
          'Residents, property owners, local businesses, staff, and elected officials may all approach the issue from different perspectives.',
          'Outside guidance can give the conversation a clearer structure by explaining the available options, the trade-offs between them, and the practical limits of enforcement.',
          'It should not replace local decision-making. The community must still establish its own priorities and decide which outcomes matter most.',
          'But those decisions are often stronger when they are informed by a practical understanding of what different regulations can\u2014and cannot\u2014accomplish.',
          'Communities looking for unbiased guidance based on their own goals and existing rules are welcome to reach out to Civic Rentals LLC.',
        ],
      },
    ],
  },
  {
    slug: 'str-frameworks-worth-watching',
    title: 'Several STR Frameworks Worth Watching',
    date: 'June 2026',
    publishedDate: '2026-06-01',
    updatedDate: '2026-08-02',
    author: 'Stacy Pobatschnig',
    authorUrl: 'https://www.linkedin.com/in/stacypobatschnig',
    summary: 'A growing number of local governments are distinguishing between resident hosting and investor-operated short-term rentals. Here are several frameworks worth watching.',
    content: [
      'In most communities debating short-term rentals, the central issue is not whether residents should be allowed to occasionally rent a spare room or their own home.',
      'The more difficult question is whether residential properties should be converted into full-time lodging businesses operated by investors who do not live there.',
      'A growing number of local governments are trying to address that distinction directly. Their regulations differ, but the underlying principle is similar: resident hosting and investor-operated short-term rentals are not necessarily the same land use and do not always need to be regulated in the same way.',
    ],
    sections: [
      {
        heading: 'Drawing a Line Between Hosting and Commercial Lodging',
        paragraphs: [
          'Traditional short-term rental rules often place every operator into one category. A homeowner renting a bedroom for several weekends may be treated the same as an investor operating multiple whole-home rentals throughout the year.',
          'Several governments have moved away from that approach.',
          'Bar Harbor, Maine, separates short-term rentals into two categories. VR-1 rentals must be the owner\'s primary residence or located on the same property as the owner\'s primary residence. VR-2 rentals are entire homes that are not the owner\'s primary residence. To qualify for VR-1 status, the owner must reside at the property for more than 183 days each year and use it as a legal address.',
          'Burlington, Vermont, similarly structures its rules around the relationship between the host and the property. In most circumstances, the rental must be within the host\'s primary residence or in another unit on the same lot or in the same building. Off-site rentals are permitted only under limited exceptions, including certain seasonal homes and properties that also provide qualifying affordable housing.',
          'Buffalo, New York, allows both owner-occupied and non-owner-occupied rentals, but it does not treat them identically. Every short-term rental must be licensed, while non-owner-occupied properties must also obtain a special-use permit and comply with applicable zoning requirements. The city defines owner occupancy partly by whether the property is the owner\'s permanent residence and whether the owner lives there for more than six months of the year.',
          'Atlanta takes another variation on the same idea. Its licensing framework is tied to the operator\'s primary residence, although one license may include both the primary residence and one additional dwelling. The primary residence must be registered first.',
          'These are not identical policies. Some governments restrict non-owner-occupied rentals, some subject them to additional review, and others allow a limited number of additional properties. But each framework begins with the same threshold question:',
          'Does someone actually live here, or is the property primarily being operated as commercial lodging?',
        ],
      },
      {
        heading: 'Why the Distinction Matters',
        paragraphs: [
          'The distinction is important because the two activities can have different effects on a housing market.',
          'A resident renting a room, accessory unit, or primary home for part of the year does not necessarily remove a housing unit from the long-term market. In many cases, the property remains someone\'s home.',
          'A whole-home rental operated year-round by an absentee owner is different. The property may function more like a small lodging business than a residence, even when it remains legally classified as housing.',
          'Treating those activities identically can produce regulations that are either too restrictive or too permissive. A blanket prohibition may unnecessarily limit ordinary homeowners, while an unrestricted system may allow residential properties to be steadily converted into investor-operated accommodations.',
          'Primary-residence classifications give governments a way to regulate the commercial segment without eliminating resident hosting.',
        ],
      },
      {
        heading: 'Different Communities, Different Regulatory Tools',
        paragraphs: [
          'The examples also show that governments do not need to use one universal formula.',
          'Bar Harbor uses clearly defined operating categories.',
          'Burlington generally requires the host to live on or near the rental property, while allowing narrow exceptions tied to seasonal housing and affordable housing.',
          'Buffalo permits non-owner-occupied rentals but subjects them to a higher level of land-use review.',
          'Atlanta connects licensing to a primary residence while allowing a limited additional dwelling.',
          'Other communities may choose zoning restrictions, density limits, permit attrition, ownership limits, or special-use approvals. The appropriate structure will depend on the local housing market, tourism economy, neighborhood patterns, and available enforcement capacity.',
          'The broader lesson is not that every government should copy one ordinance. It is that governments should identify the specific activity they are trying to regulate rather than treating every short-term rental as interchangeable.',
        ],
      },
      {
        heading: 'Registration Is the Foundation',
        paragraphs: [
          'Whatever distinction a government adopts, it should be supported by a registration process that is clear, practical, and easy to follow.',
          'For resident property owners, obtaining a permit should not require navigating an overly complex or expensive process. The requirements should be proportionate to the activity, with straightforward applications, reasonable fees, and clearly explained obligations.',
          'Professional investors and commercial operators can reasonably be subject to a different standard. These operators may be required to complete additional reviews, meet stricter operating conditions, obtain special approvals, or comply with limits that do not apply to residents renting their primary homes.',
          'A tiered registration system allows governments to recognize the difference between occasional resident hosting and the operation of short-term rentals as a commercial business. It also makes the rules easier to understand by connecting each type of operator with a clearly defined permit category and set of requirements.',
          'The effectiveness of the framework depends not only on the restrictions it creates, but also on whether the registration process is simple enough for ordinary property owners to use and structured enough to apply meaningful standards to professional operators.',
        ],
      },
      {
        heading: 'The Broader Lesson',
        paragraphs: [
          'Communities facing housing pressure are often presented with two unsatisfying options: prohibit short-term rentals entirely or allow unrestricted commercial growth.',
          'These frameworks point toward a third approach.',
          'Require registration. Make the permit process clear and accessible. Define primary residence precisely. Distinguish resident hosting from investor-operated lodging. Then apply requirements that reflect the scale and commercial nature of each activity.',
        ],
      },
      {
        heading: 'Sources',
        paragraphs: [
          'Town of Bar Harbor, Maine, official short-term rental guidance and registration materials.',
          'City of Burlington, Vermont, Short-Term Rental Frequently Asked Questions and Chapter 18 guidance.',
          'City of Buffalo, New York, Short-Term Rental Dwelling Code and licensing guidance.',
          'City of Atlanta, Short-Term Rental Program guidance and licensing information.',
        ],
      },
    ],
  },
  {
    slug: 'home-sharing-to-commercial',
    title: 'From Home Sharing to Commercial STR Markets: A Global Shift',
    date: 'June 2026',
    publishedDate: '2026-06-01',
    updatedDate: '2026-08-02',
    author: 'Stacy Pobatschnig',
    authorUrl: 'https://www.linkedin.com/in/stacypobatschnig',
    summary: 'Across markets worldwide, short-term rentals are shifting from casual home sharing toward professional, portfolio-style operations — and regulation hasn\'t caught up.',
    content: [
      'Short-term rental markets are changing — but the way we talk about them hasn\'t fully caught up.',
      'For years, the dominant narrative was simple: people occasionally renting out spare rooms, or listing their homes while away. That framing still exists, and in some places it remains true.',
      'But it is no longer the full picture.',
      'Across markets, there is a clear shift toward:',
    ],
    sections: [
      {
        list: [
          'Entire-home listings',
          'Repeat operators',
          'Portfolio-style ownership',
          'Professional management',
        ],
        after: [
          'In many places, short-term rentals now look less like casual home sharing and more like distributed commercial accommodation operating within residential housing stock.',
          'That shift matters — because regulation built for one model doesn\'t map cleanly onto the other.',
          'When all short-term rentals are treated as if they are the same, it becomes difficult to:',
        ],
      },
      {
        list: [
          'Understand who is actually operating',
          'Distinguish between occasional hosts and commercial actors',
          'Design effective permitting and registration systems',
          'Enforce rules in a consistent way',
        ],
        after: [
          'A host renting out a spare bedroom occasionally is fundamentally different from an operator managing multiple units across a city. But in practice, those distinctions are often blurred in both policy and data.',
          'This is where many programs start to struggle — not because of a lack of effort, but because the underlying assumptions no longer match the structure of the market.',
          'A growing body of research across Europe and beyond is pointing to this same shift toward professionalization and more commercial activity. This isn\'t about any one platform — it\'s a broader structural change in how the market operates.',
          'If you\'re working on short-term rental policy, enforcement, or program design, we\'d be interested to hear how you\'re thinking about this — feel free to reach out or share your perspective.',
        ],
      },
    ],
  },
  {
    slug: 'top-5-challenges',
    title: 'Top 5 STR Program Challenges for Governments',
    date: 'May 2026',
    publishedDate: '2026-05-01',
    updatedDate: '2026-08-02',
    author: 'Stacy Pobatschnig',
    authorUrl: 'https://www.linkedin.com/in/stacypobatschnig',
    summary: 'From visibility gaps to resource constraints, here are the five recurring themes we see across hundreds of government STR programs.',
    content: [
      'Short-term rental regulation has moved up the priority list for governments around the world. Cities, towns, and national authorities are under growing pressure from residents, housing advocates, and elected officials to get a handle on the sector.',
      'But wanting to regulate STRs and being able to regulate them effectively are two very different things.',
      'Civic Rentals LLC was founded on nearly a decade of direct experience working with hundreds of governments on this issue. The same themes come up time and again — regardless of jurisdiction size, geography, or how long a program has been running.',
      'Here are the top 5 challenges governments face managing short-term rental programs:',
    ],
    sections: [
      {
        heading: '1. The Visibility Problem',
        paragraphs: [
          'Before governments can manage STR activity, they need to understand it. In practice, many are making decisions based on partial or outdated information — and the gap between what\'s happening on the ground and what government systems can see is often larger than expected.',
        ],
      },
      {
        heading: '2. The Compliance Gap',
        paragraphs: [
          'Having rules on the books is not the same as being able to enforce them. The distance between policy and day-to-day operations is where most programs begin to break down — and closing that gap requires more than policy design alone.',
        ],
      },
      {
        heading: '3. Community Pressure',
        paragraphs: [
          'Residents often become the first signal that STR activity is happening at all. Managing that pressure — and turning it into something actionable — is an ongoing operational challenge that consumes significant staff time and political bandwidth.',
        ],
      },
      {
        heading: '4. The Revenue Question',
        paragraphs: [
          'STRs represent a revenue opportunity for governments that is frequently under-realized. Not because the opportunity isn\'t there, but because capturing it consistently requires coordination across systems and teams that aren\'t always aligned.',
        ],
      },
      {
        heading: '5. The Resource Constraint',
        paragraphs: [
          'All of this has to happen within the limits of what government teams can realistically support. STR oversight rarely comes with dedicated staff or budget — it\'s layered onto existing responsibilities, and the outcomes reflect that reality.',
        ],
      },
      {
        paragraphs: [
          'None of these challenges are insurmountable. But the governments that are making meaningful progress tend to approach them differently — starting with a clear understanding of where the gaps actually are.',
          'If your team is navigating any of these challenges, we\'d love to talk.',
        ],
      },
    ],
  },
];
